import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { resolveLocale } from '../../utils/formatLocalDate';

// Значение наружу и внутрь — всегда ISO "YYYY-MM-DD" (или ''), а на экране — формат языка:
// uk → DD.MM.YYYY, en-US → MM/DD/YYYY, en-GB → DD/MM/YYYY, ja → YYYY/MM/DD

const LEN = { day: 2, month: 2, year: 4 };
const PLACEHOLDER = { day: 'DD', month: 'MM', year: 'YYYY' };
const FALLBACK = { order: ['day', 'month', 'year'], sep: '.' };
const pad = (n, len = 2) => String(n).padStart(len, '0');

const getPattern = (locale) => {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).formatToParts(new Date(2000, 10, 22));
    const order = parts.filter((p) => LEN[p.type]).map((p) => p.type);
    const sep = (parts.find((p) => p.type === 'literal')?.value || '').trim() || '.';
    return order.length === 3 ? { order, sep } : FALLBACK;
  } catch {
    return FALLBACK;
  }
};

const isoToText = (iso, { order, sep }) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return '';
  const v = { year: m[1], month: m[2], day: m[3] };
  return order.map((k) => v[k]).join(sep);
};

// "221" → "22.1": разделители ставим сами, лишних на конце не оставляем (удаление работает)
const maskDigits = (digits, { order, sep }) => {
  const out = [];
  let i = 0;
  for (const k of order) {
    const chunk = digits.slice(i, i + LEN[k]);
    if (!chunk) break;
    out.push(chunk);
    i += LEN[k];
  }
  return out.join(sep);
};

const textToIso = (text, { order }) => {
  const digits = text.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const v = {};
  let i = 0;
  for (const k of order) {
    v[k] = parseInt(digits.slice(i, i + LEN[k]), 10);
    i += LEN[k];
  }
  const d = new Date(v.year, v.month - 1, v.day);
  const valid =
    v.year >= 1900 &&
    d.getFullYear() === v.year && d.getMonth() === v.month - 1 && d.getDate() === v.day;
  return valid ? `${pad(v.year, 4)}-${pad(v.month)}-${pad(v.day)}` : null;
};

export const LocalDateInput = ({ value, onChange, className = '', id, placeholder }) => {
  const { t, ...i18n } = useTranslation();
  const locale = resolveLocale(i18n);
  const pattern = useMemo(() => getPattern(locale), [locale]);

  const [text, setText] = useState(() => isoToText(value, pattern));
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const pickerRef = useRef(null);

  // значение изменили снаружи (открыли другую пару, сменился язык) — перерисовываем текст
  useEffect(() => {
    setText((cur) => ((textToIso(cur, pattern) ?? '') === (value || '') ? cur : isoToText(value, pattern)));
  }, [value, pattern]);

  const hasText = text.replace(/\D/g, '').length > 0;
  const invalid = hasText && textToIso(text, pattern) === null;

  // неполная или несуществующая дата блокирует отправку формы нативной валидацией
  useEffect(() => {
    inputRef.current?.setCustomValidity(invalid ? t('common.date_invalid') : '');
  });

  const handleChange = (e) => {
    const raw = e.target.value;

    // вставили ISO-дату целиком
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const next = isoToText(raw, pattern);
      setText(next);
      const iso = textToIso(next, pattern);
      if (iso) onChange(iso);
      return;
    }

    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const next = maskDigits(digits, pattern);
    setText(next);
    if (digits.length === 0) {
      onChange('');
    } else {
      const iso = textToIso(next, pattern);
      if (iso) onChange(iso);      // пока дата не полная, в форме остаётся прежнее значение
    }
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      if (el.showPicker) el.showPicker();
      else el.click();
    } catch {
      el.click();
    }
  };

  const hint = placeholder || pattern.order.map((k) => PLACEHOLDER[k]).join(pattern.sep);
  const bad = invalid && touched;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder={hint}
        className={`${className} pr-10 ${bad ? '!border-red-500' : ''}`}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label={t('common.pick_date')}
        className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
      >
        📅
      </button>
      {/* системный календарь; сам инпут скрыт, формат ввода задаёт поле выше */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="absolute right-0 top-full w-0 h-0 opacity-0 pointer-events-none"
      />
    </div>
  );
};